/**
 * Application Options should serve as the 'bag of options' that were pre-loaded from the query
 * params. Do not use app options as the bag of configurable options in the application.
 */

import _ from 'lodash';
import config, {siteContext} from '../config';
import moment from 'moment';

let appOptions = {
    properties: {
        brandId: siteContext?.brandId,
        brandName: siteContext?.humanReadableBrandName,
        countryCode: siteContext?.countryCode,
        currency: siteContext?.currency,
        environment: siteContext?.environment,
        locale: siteContext?.locale,
        site: siteContext?.name,
        baseBizopUrl: '/td/proxies/',
        baseBizopTdUrl: '/td/v2/proxies/',
        verifyUrl: '/cko/verify/',
        apiUrl: '/cko/api/',
        el: '#traveler-checkout',
        footerEl: '#footer',
        headerEl: '#header',
        scrollError: false, // prevents traveler payments module from scrolling to error, used for native mobile apps
        servicesTimeout: 120000,
        showHeader: true,
        showFooter: true,
    },
    initializeForRequest(options) {
        // set checkoutType
        this.set('checkoutType', config.flow);

        // set geo country code resolved server-side from the edge-agent-geolocation-info header
        this.set('geoCountryCode', config.geoCountryCode ?? null);

        // set the params
        this.processParams(config.params);
        // if there is a resume-order call get as much data out of the callback checkoutUrl param
        this.processCheckoutUrlParam();

        // initialize appoptions with passed in options
        _.each(
            options,
            (value, prop) => {
                this.set(prop, value);
            },
            this
        );
    },
    processCheckoutUrlParam() {
        if (!config.params.checkoutUrl) {
            return;
        }
        const paramsOfParam = new URL(config.params.checkoutUrl);
        const listingId = paramsOfParam.pathname.split('/').pop();
        this.set('listingId', listingId);

        let checkoutUrlParams = {}; // empty object
        if (!_.isUndefined(config.checkoutRedirectUrlParams)) {
            checkoutUrlParams = config.checkoutRedirectUrlParams; // if available use the checkoutRedirectUrlParams
        } else {
            // if the params are not available use this to pull them out of checkoutUrl param
            paramsOfParam.search
                .substr(1)
                .split('&')
                .forEach((param) => {
                    const keyValue = param.split('=');
                    checkoutUrlParams[keyValue[0]] = keyValue[1];
                });
        }

        // original dates passed in via url from outside source
        // format MM/DD/YYYY
        this.set('arrivalDate', checkoutUrlParams.arrivalDate);
        this.set('departureDate', checkoutUrlParams.departureDate);

        // convert dates for createCheckoutv3 bizops
        // format needs to be YYYY-MM-DD
        this.set(
            'checkinDate',
            moment
                .utc(checkoutUrlParams.arrivalDate, 'MM/DD/YYYY')
                .format('YYYY-MM-DD')
        );
        this.set(
            'checkoutDate',
            moment
                .utc(checkoutUrlParams.departureDate, 'MM/DD/YYYY')
                .format('YYYY-MM-DD')
        );

        this.set('adults', parseInt(checkoutUrlParams.adultsCount, 10));
        this.set('children', parseInt(checkoutUrlParams.childrenCount, 10));
        this.set(
            'infants',
            !_.isUndefined(checkoutUrlParams.infantsCount)
                ? parseInt(checkoutUrlParams.infantsCount, 10)
                : 0
        );
        this.set('petIncluded', checkoutUrlParams.petIncluded === 'true');

        // TEMP HACK FOR: https://jira.homeawaycorp.com/browse/GT-10950
        this.set(
            'unitLink',
            _.isArray(checkoutUrlParams.unitLink)
                ? checkoutUrlParams.unitLink[0]
                : checkoutUrlParams.unitLink
        );
        this.set('vrbex', checkoutUrlParams.vrbex === 'true');
    },
    processParams(params) {
        // convert dates for createCheckoutv3 bizops
        this.set('checkinDate', config.checkinDate);
        this.set('checkoutDate', config.checkoutDate);

        // original dates passed in via url from outside source
        this.set('arrivalDate', params.arrivalDate);
        this.set('departureDate', params.departureDate);

        this.set('adults', parseInt(params.adultsCount, 10));
        this.set('children', parseInt(params.childrenCount, 10));
        this.set(
            'infants',
            !_.isUndefined(params.infantsCount)
                ? parseInt(params.infantsCount, 10)
                : 0
        );
        this.set('petIncluded', params.petIncluded === 'true');

        // TEMP HACK FOR: https://jira.homeawaycorp.com/browse/GT-10950
        this.set(
            'unitLink',
            _.isArray(params.unitLink) ? params.unitLink[0] : params.unitLink
        );
        this.set('vrbex', params.vrbex === 'true');
        this.set('ratePlanId', params.ratePlanId);
        this.set('roomTypeId', params.roomTypeId);

        if (!_.isUndefined(params.cid)) {
            this.set('cid', params.cid);
        }
        if (!_.isUndefined(params.hacid)) {
            this.set('hacid', params.hacid);
        }
        if (!_.isUndefined(params.nativeCheckoutEligibleV1)) {
            // passed in by the native app to configure features
            // which help to accurately compare analytics
            // with native checkout
            this.set(
                'nativeCheckoutEligibleV1',
                params.nativeCheckoutEligibleV1 === 'true'
            );
        }

        // confirmation
        this.set(
            'reservationReferenceNumber',
            params.reservationReferenceNumber
        );
        this.set('reservationId', params.reservationId);

        // online payments
        this.set('quoteGuid', params.quoteGuid);
        this.set('inquiryUuid', params.inquiryUuid);
        this.set('quotePaymentSchedule', params.quotePaymentSchedule);
        this.set('listingId', params.listingId);

        // vas cart
        this.set('reservationUrl', params.reservationLink);
        this.set('externalPMI', params.externalPMI);
        this.set('orderSource', params.orderSource);
        this.set('travelerOrderUrl', params.travelerOrderUrl);
        this.set('listingTriad', params.listingTriad);
        this.set('source', params.source);
        if (!_.isUndefined(params.inferredCountryCode)) {
            // To avoid the introduction of XSS vulnerabilities, if any future client-side consumer that reads appOptions.get('inferredCountryCode') and renders it into the DOM without encoding
            const raw = params.inferredCountryCode;
            this.set(
                'inferredCountryCode',
                /^[A-Z]{2}$/.test(raw) ? raw : null
            );
        }

        // checkout
        this.set('analyticsUrl', config.analyticsUrl);

        // distributionChannel
        _.extend(this.properties, config.cid || {});

        if (_.isUndefined(this.properties.distributionChannel)) {
            this.set('distributionChannel', null);
        }

        // enhancementPlacement
        _.extend(this.properties, config.hacid || {});

        if (_.isUndefined(this.properties.distributionId)) {
            this.set('distributionId', null);
        }

        if (_.isUndefined(this.properties.originationDate)) {
            this.set('originationDate', null);
        }

        return this;
    },
    set(prop, value) {
        this.properties[prop] = value;
    },
    remove(prop) {
        delete this.properties[prop];
    },
};

export default {
    initializeForRequest: _.bind(appOptions.initializeForRequest, appOptions),
    set: _.bind(appOptions.set, appOptions),
    remove: _.bind(appOptions.remove, appOptions),
    get(prop) {
        return _.isString(appOptions.properties[prop])
            ? decodeURI(appOptions.properties[prop])
            : appOptions.properties[prop];
    },
    xgetProperties() {
        return appOptions.properties;
    },
};
